/**
 * Capital Readiness Score (CRS) service - Phase 8
 */
import api from './api';
import { ApiResponse } from '../types';
import {
  CapitalReadinessScore,
  CRSListFilters,
  CRSListResponse,
  CRSLeaderboardEntry,
  ScoreRequest,
  ServiceProvider,
  ProviderListFilters,
  CreateProviderRequest,
} from '../types/capitalReadiness';

function errMsg(e: any, fallback: string): string {
  return e?.response?.data?.detail || e?.message || fallback;
}

export async function scoreEntity(
  req: ScoreRequest
): Promise<ApiResponse<CapitalReadinessScore>> {
  try {
    const res = await api.post('/capital-readiness/score', req);
    return { data: res.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to score entity') };
  }
}

export async function fetchScores(
  filters?: CRSListFilters
): Promise<ApiResponse<CRSListResponse>> {
  try {
    const res = await api.get('/capital-readiness/scores', { params: filters });
    const payload = res.data;
    if (Array.isArray(payload)) {
      return {
        data: {
          scores: payload,
          total: payload.length,
          page: 1,
          size: payload.length,
          pages: 1,
        },
      };
    }
    return {
      data: {
        scores: payload?.scores || payload?.items || [],
        total: payload?.total ?? 0,
        page: payload?.page ?? 1,
        size: payload?.size ?? 50,
        pages: payload?.pages ?? 1,
      },
    };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to load readiness scores') };
  }
}

export async function fetchScore(
  id: number
): Promise<ApiResponse<CapitalReadinessScore>> {
  try {
    const res = await api.get(`/capital-readiness/scores/${id}`);
    return { data: res.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to load score') };
  }
}

export async function fetchLatest(params: {
  startup_id?: number;
  project_id?: number;
}): Promise<ApiResponse<CapitalReadinessScore | null>> {
  try {
    const res = await api.get('/capital-readiness/latest', { params });
    return { data: res.data || null };
  } catch (e: any) {
    // 404 -> null
    if (e?.response?.status === 404) {
      return { data: null };
    }
    return { error: errMsg(e, 'Failed to load latest score') };
  }
}

export async function fetchHistory(params: {
  startup_id?: number;
  project_id?: number;
}): Promise<ApiResponse<CapitalReadinessScore[]>> {
  try {
    const res = await api.get('/capital-readiness/history', { params });
    const payload = res.data;
    if (Array.isArray(payload)) return { data: payload };
    return { data: payload?.scores || payload?.items || [] };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to load score history') };
  }
}

export async function rescore(
  id: number
): Promise<ApiResponse<CapitalReadinessScore>> {
  try {
    const res = await api.post(`/capital-readiness/scores/${id}/rescore`);
    return { data: res.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to rescore') };
  }
}

export async function fetchLeaderboard(
  limit: number = 20
): Promise<ApiResponse<CRSLeaderboardEntry[]>> {
  try {
    const res = await api.get('/capital-readiness/leaderboard', {
      params: { limit },
    });
    const payload = res.data;
    if (Array.isArray(payload)) return { data: payload };
    return { data: payload?.entries || payload?.items || [] };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to load leaderboard') };
  }
}

export async function fetchProviders(
  filters?: ProviderListFilters
): Promise<ApiResponse<ServiceProvider[]>> {
  try {
    const res = await api.get('/capital-readiness/providers', {
      params: filters,
    });
    const payload = res.data;
    if (Array.isArray(payload)) return { data: payload };
    return { data: payload?.providers || payload?.items || [] };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to load providers') };
  }
}

export async function createProvider(
  req: CreateProviderRequest
): Promise<ApiResponse<ServiceProvider>> {
  try {
    const res = await api.post('/capital-readiness/providers', req);
    return { data: res.data };
  } catch (e: any) {
    return { error: errMsg(e, 'Failed to create provider') };
  }
}

const capitalReadinessService = {
  scoreEntity,
  fetchScores,
  fetchScore,
  fetchLatest,
  fetchHistory,
  rescore,
  fetchLeaderboard,
  fetchProviders,
  createProvider,
};

export default capitalReadinessService;
